import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, Button, ButtonProps, Heading, HeadingProps, Image, List, ListRootProps, Separator, Stack, StackProps, Text, TextProps, VStack } from '@chakra-ui/react';
import { ButtonStack } from '@src/components/sco/ButtonStack';
import { Paragraph } from '@src/components/sco/Paragraph';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import React from 'react';
import { LuChevronRight, LuCircleCheck } from 'react-icons/lu';
export type GenericContentBlockOrientation = 'text-left' | 'text-right';
export interface GenericContentBlockCta {
  label: string;
  /**
   * Navigate to a URL. Omit when `onClick` alone should handle the action
   * (e.g. open the page diagnostic in a modal).
   */
  href?: string;
  /** Click handler. When both `href` and `onClick` are set, both run (link navigation + handler). */
  onClick?: () => void;
  buttonProps?: ButtonProps;
}
export interface GenericContentBlockProps {
  /** Small uppercase-style label under the company name (e.g. "TOTAL RAISED"). */
  eyebrow?: string;
  heading: string;
  paragraph: string;
  /** Optional checkmark bullet points rendered below the paragraph. */
  listItems?: string[];
  primaryCta: GenericContentBlockCta;
  secondaryCta?: GenericContentBlockCta;
  /**
   * Which side the text content sits on at desktop.
   * Defaults to `text-left`. Alternate between blocks for a zigzag layout.
   */
  orientation?: GenericContentBlockOrientation;
  /**
   * Visual element rendered opposite the text — image, video, custom component, etc.
   * Takes precedence over `imageSrc` / `videoSrc` / `children`.
   */
  visual?: React.ReactNode;
  /** Convenience image when you don't need a custom visual node. */
  imageSrc?: string;
  imageAlt?: string;
  /** Convenience video when you don't need a custom visual node. */
  videoSrc?: string;
  /** Alias for `visual` when nesting JSX as children. */
  children?: React.ReactNode;
  containerProps?: StackProps;
  textProps?: StackProps;
  visualProps?: BoxProps;
  headingProps?: HeadingProps;
  paragraphProps?: TextProps;
  listProps?: ListRootProps;
  buttonStackProps?: StackProps;
  eyebrowProps?: TextProps;
}
function resolveDirection(orientation: GenericContentBlockOrientation, getBreakpointValue: (values: unknown[]) => unknown) {
  // Mobile/tablet: visual on top (column-reverse). Desktop: row based on orientation.
  if (orientation === 'text-right') {
    return getBreakpointValue(['column-reverse', 'column-reverse', 'column-reverse', 'row-reverse']);
  }
  return getBreakpointValue(['column-reverse', 'column-reverse', 'column-reverse', 'row']);
}
function renderVisual(props: {
  visual?: React.ReactNode;
  children?: React.ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  videoSrc?: string;
}) {
  if (props.visual) {
    return props.visual;
  }
  if (props.children) {
    return props.children;
  }
  if (props.videoSrc) {
    return <Box borderRadius={'2xl'} overflow={'hidden'} width={'full'}>
        <video controls src={props.videoSrc} style={{
        width: '100%',
        display: 'block'
      }} />
      </Box>;
  }
  if (props.imageSrc) {
    return <Image alt={props.imageAlt || ''} borderRadius={'2xl'} height={'auto'} overflow={'hidden'} src={props.imageSrc} width={'full'} />;
  }
  return null;
}

/**
 * GenericContentBlock – A two-sided content row for use inside GenericSection.
 * Text (heading, paragraph, CTAs) on one side; image / video / custom visual on the other.
 * Stacks vertically on mobile/tablet and horizontally on desktop.
 */
export const GenericContentBlock: React.FC<GenericContentBlockProps> = t0 => {
  const $ = _c(59);
  const {
    eyebrow,
    heading,
    paragraph,
    listItems,
    primaryCta,
    secondaryCta,
    orientation: t1,
    visual,
    imageSrc,
    imageAlt,
    videoSrc,
    children,
    containerProps,
    textProps,
    visualProps,
    headingProps,
    paragraphProps,
    listProps,
    buttonStackProps,
    eyebrowProps
  } = t0;
  const orientation = t1 === undefined ? "text-left" : t1;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t2;
  if ($[0] !== children || $[1] !== imageAlt || $[2] !== imageSrc || $[3] !== videoSrc || $[4] !== visual) {
    t2 = renderVisual({
      visual,
      children,
      imageSrc,
      imageAlt,
      videoSrc
    });
    $[0] = children;
    $[1] = imageAlt;
    $[2] = imageSrc;
    $[3] = videoSrc;
    $[4] = visual;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const resolvedVisual = t2;
  let t3;
  if ($[6] !== getBreakpointValue || $[7] !== orientation) {
    t3 = resolveDirection(orientation, getBreakpointValue);
    $[6] = getBreakpointValue;
    $[7] = orientation;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== getBreakpointValue) {
    t4 = getBreakpointValue([8, 8, 10, 12, 16]);
    $[9] = getBreakpointValue;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== eyebrow || $[12] !== eyebrowProps) {
    t5 = eyebrow && <Text color="yellow.500" textStyle="labelXs" {...eyebrowProps}>{eyebrow}</Text>;
    $[11] = eyebrow;
    $[12] = eyebrowProps;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== getBreakpointValue) {
    t6 = getBreakpointValue(["2xl", "2xl", "3xl", "3xl", "4xl"]);
    $[14] = getBreakpointValue;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== heading || $[17] !== headingProps || $[18] !== t6) {
    t7 = <Heading as="h3" size={t6} {...headingProps}>{heading}</Heading>;
    $[16] = heading;
    $[17] = headingProps;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== getBreakpointValue) {
    t8 = getBreakpointValue(["md", "md", "lg", "lg", "lg"]);
    $[20] = getBreakpointValue;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== paragraph || $[23] !== paragraphProps || $[24] !== t8) {
    t9 = <Paragraph color="colorPalette.fg/70" fontSize={t8} {...paragraphProps}>{paragraph}</Paragraph>;
    $[22] = paragraph;
    $[23] = paragraphProps;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== getBreakpointValue || $[27] !== listItems || $[28] !== listProps) {
    t10 = listItems && listItems.length > 0 && <List.Root color="colorPalette.fg/70" fontSize={getBreakpointValue(["md", "md", "lg", "lg", "xl"])} gap={3} marginY={2} variant="plain" {...listProps}>{listItems.map(_temp)}</List.Root>;
    $[26] = getBreakpointValue;
    $[27] = listItems;
    $[28] = listProps;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== buttonStackProps) {
    t11 = {
      marginTop: 2,
      ...buttonStackProps
    };
    $[30] = buttonStackProps;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== primaryCta.buttonProps || $[33] !== primaryCta.href || $[34] !== primaryCta.label || $[35] !== primaryCta.onClick) {
    t12 = primaryCta.href ? <Button size="lg" variant="cta" {...primaryCta.buttonProps} asChild={true}><NextLink href={primaryCta.href} onClick={primaryCta.onClick}>{primaryCta.label}<LuChevronRight /></NextLink></Button> : <Button size="lg" variant="cta" {...primaryCta.buttonProps} onClick={primaryCta.onClick}>{primaryCta.label}<LuChevronRight /></Button>;
    $[32] = primaryCta.buttonProps;
    $[33] = primaryCta.href;
    $[34] = primaryCta.label;
    $[35] = primaryCta.onClick;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== secondaryCta) {
    t13 = secondaryCta && (secondaryCta.href ? <Button size="lg" variant="outlineGhost" {...secondaryCta.buttonProps} asChild={true}><NextLink href={secondaryCta.href} onClick={secondaryCta.onClick}>{secondaryCta.label}</NextLink></Button> : <Button size="lg" variant="outlineGhost" {...secondaryCta.buttonProps} onClick={secondaryCta.onClick}>{secondaryCta.label}</Button>);
    $[37] = secondaryCta;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t11 || $[40] !== t12 || $[41] !== t13) {
    t14 = <ButtonStack stackProps={t11}>{t12}{t13}</ButtonStack>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t10 || $[44] !== t14 || $[45] !== t5 || $[46] !== t7 || $[47] !== t9 || $[48] !== textProps) {
    t15 = <VStack align="start" flex={1} gap={4} justify="center" width="full" {...textProps}>{t5}{t7}{t9}{t10}{t14}</VStack>;
    $[43] = t10;
    $[44] = t14;
    $[45] = t5;
    $[46] = t7;
    $[47] = t9;
    $[48] = textProps;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== resolvedVisual || $[51] !== visualProps) {
    t16 = resolvedVisual && <Box flex={1} width="full" {...visualProps}>{resolvedVisual}</Box>;
    $[50] = resolvedVisual;
    $[51] = visualProps;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== containerProps || $[54] !== t15 || $[55] !== t16 || $[56] !== t3 || $[57] !== t4) {
    t17 = <Stack align="center" direction={t3} gap={t4} width="full" {...containerProps}>{t15}{t16}</Stack>;
    $[53] = containerProps;
    $[54] = t15;
    $[55] = t16;
    $[56] = t3;
    $[57] = t4;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  return t17;
};
export type GenericContentBlockConfig = Omit<GenericContentBlockProps, 'orientation' | 'children'>;
export interface GenericContentBlocksProps {
  blocks: GenericContentBlockConfig[];
  /** Starting orientation. Subsequent blocks alternate. Defaults to `text-left`. */
  startOrientation?: GenericContentBlockOrientation;
  /** Applied to any block that doesn't provide its own visual / image / video. */
  defaultVisual?: React.ReactNode;
}

/**
 * GenericContentBlocks – Renders GenericContentBlock items with automatic
 * left/right orientation alternation and a Separator between each block.
 * Relies on the parent (e.g. GenericSection) for vertical spacing between siblings.
 */
export const GenericContentBlocks: React.FC<GenericContentBlocksProps> = t0 => {
  const $ = _c(10);
  const {
    blocks,
    startOrientation: t1,
    defaultVisual
  } = t0;
  const startOrientation = t1 === undefined ? "text-left" : t1;
  let t2;
  if ($[0] !== blocks || $[1] !== defaultVisual || $[2] !== startOrientation) {
    let t3;
    if ($[4] !== blocks.length || $[5] !== defaultVisual || $[6] !== startOrientation) {
      t3 = (block, index) => {
        const isEven = index % 2 === 0;
        const orientation = startOrientation === "text-left" ? isEven ? "text-left" : "text-right" : isEven ? "text-right" : "text-left";
        const hasOwnVisual = !!(block.visual || block.imageSrc || block.videoSrc);
        const isLast = index === blocks.length - 1;
        return <React.Fragment key={`${block.heading}-${index}`}><GenericContentBlock {...block} orientation={orientation} {...!hasOwnVisual && defaultVisual ? {
            visual: defaultVisual
          } : null} />{!isLast && <Separator />}</React.Fragment>;
      };
      $[4] = blocks.length;
      $[5] = defaultVisual;
      $[6] = startOrientation;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = blocks.map(t3);
    $[0] = blocks;
    $[1] = defaultVisual;
    $[2] = startOrientation;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <>{t2}</>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export default GenericContentBlock;
function _temp(item) {
  return <List.Item key={item} alignItems="center" fontSize="xs"><List.Indicator asChild={true} color="yellow.500"><LuCircleCheck /></List.Indicator>{item}</List.Item>;
}